import React, { useState, useEffect } from 'react';
import { FlaskConical, Globe, Sparkles } from 'lucide-react';
import { api } from '../api';
import { CourseDetailModal } from '../components/modals/CourseDetailModal';
import { useTranslation } from 'react-i18next';

const WhatsNewPage = ({ user }) => {
  const { t } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);

  useEffect(() => {
    const fetchNew = async () => {
      try {
        const endpoint = ['training_partner', 'partner_admin'].includes(user?.role)
          ? '/courses/with-download-info'
          : '/courses';
        const data = await api.request(endpoint);
        const all = Array.isArray(data) ? data : data.courses || [];
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        const fresh = all.filter(c => c.created_at && new Date(c.created_at) >= thirtyDaysAgo);
        setCourses(fresh);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchNew();
  }, [user?.role]);

  const getBadgeStyle = (courseType) => {
    const map = {
      'Train-the-Trainer': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'On-Demand': 'bg-blue-50 text-blue-700 border-blue-200',
      'ILT': 'bg-purple-50 text-purple-700 border-purple-200',
      'Workshop': 'bg-amber-50 text-amber-700 border-amber-200',
    };
    return map[courseType] || 'bg-gray-50 text-gray-600 border-gray-200';
  };

  return (
    <div className="space-y-4">
      <div className="bg-levelup-dark rounded-xl p-4 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-levelup-accent/20 rounded-lg flex items-center justify-center">
            <Sparkles className="text-levelup-accent" size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold">{t('whatsNew.title')}</h1>
            <p className="text-gray-300 text-sm mt-0.5">{t('whatsNew.description')}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="animate-spin w-10 h-10 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">{t('whatsNew.loading')}</p>
        </div>
      ) : courses.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
          <Sparkles className="mx-auto text-gray-300 mb-4" size={48} />
          <p className="text-gray-600 font-medium text-lg">{t('whatsNew.allCaughtUp')}</p>
          <p className="text-gray-400 text-sm mt-1">{t('whatsNew.noNewCourses')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">{courses.length} {courses.length !== 1 ? t('whatsNew.newCoursePlural') : t('whatsNew.newCourseSingular')}</span>
            <span className="text-xs text-gray-400">{t('whatsNew.last30Days')}</span>
          </div>
          <div className="divide-y divide-gray-50">
            {courses.map((course, idx) => (
              <div
                key={course.id}
                className="px-5 py-4 flex items-start gap-4 hover:bg-gray-50 transition-colors group cursor-pointer"
                onClick={() => setSelectedCourse(course)}
                data-testid={`whats-new-course-${course.id}`}
              >
                {/* Index badge */}
                <div className="w-7 h-7 rounded-full bg-levelup-accent/10 text-levelup-dark font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-levelup-dark leading-tight group-hover:text-levelup-teal transition-colors line-clamp-2">
                    {course.title}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {course.solution_area && (
                      <span className="text-xs text-gray-600 flex items-center gap-1">
                        <Globe size={11} className="text-gray-400" />{course.solution_area}
                      </span>
                    )}
                    {course.course_type && (
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getBadgeStyle(course.course_type)}`}>
                        {course.course_type}
                      </span>
                    )}
                    {course.level && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{course.level}</span>
                    )}
                    {course.hands_on_lab && (
                      <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full flex items-center gap-1 border border-blue-100">
                        <FlaskConical size={10} />{t('whatsNew.lab')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-xs text-gray-400">
                    {course.created_at ? new Date(course.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                  </span>
                  <span className="text-xs text-levelup-teal font-medium opacity-0 group-hover:opacity-100 transition-opacity">{t('whatsNew.viewArrow')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedCourse && (
        <CourseDetailModal
          course={selectedCourse}
          onClose={() => setSelectedCourse(null)}
          hasAccess={['admin', 'content_admin', 'ms_stakeholder'].includes(user?.role) || user?.is_approved === true}
          user={user}
        />
      )}
    </div>
  );
};

// Main App Component

export default WhatsNewPage;
